import { api } from "./api";

/** Create a Cashfree deposit order and return { order_id, payment_url } */
export async function createDepositOrder(rentalId, tenantSharePaise = 0) {
  const body = tenantSharePaise > 0 ? { tenant_share: tenantSharePaise / 100 } : {};
  const { data } = await api.post(`/rentals/${rentalId}/payments/deposit/order`, body);
  return data;
}

/** How much (if anything) the current user owes toward the agreement fee, and whether
 * they've already paid it. Server-computed - drives the Sign vs Pay & Sign UI. */
export async function getAgreementFeeStatus(agreementId) {
  const { data } = await api.get(`/agreements/${agreementId}/fee-status`);
  return data; // { role, amount_owed_paise, already_paid, requires_payment }
}

/** Create a Cashfree order for the current user's share of the agreement fee. */
export async function createAgreementFeeOrder(agreementId) {
  const { data } = await api.post("/agreements/payments/agreement-fee/order", { agreement_id: agreementId });
  return data; // { success, order_id, payment_url, session_id, amount_paise }
}

/** Verify payment status after Cashfree redirect. Polls until resolved or timeout. */
export async function verifyPayment(orderId) {
  const { data } = await api.get(`/payments/verify?order_id=${orderId}`);
  return data; // { status: "success"|"pending"|"failed", rental_id, order_id }
}

/** Get full payment record */
export async function getPayment(orderId) {
  const { data } = await api.get(`/payments/${orderId}`);
  return data;
}

/** List all payments for current user */
export async function listPayments() {
  const { data } = await api.get("/payments");
  return data;
}

const CASHFREE_SDK_URL = "https://sdk.cashfree.com/js/v3/cashfree.js";

let cashfreeSDKPromise = null;

/** Load Cashfree's JS SDK once and cache the promise. */
function loadCashfreeSDK() {
  if (window.Cashfree) return Promise.resolve(window.Cashfree);
  if (cashfreeSDKPromise) return cashfreeSDKPromise;

  cashfreeSDKPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = CASHFREE_SDK_URL;
    script.onload = () => resolve(window.Cashfree);
    script.onerror = () => reject(new Error("Failed to load Cashfree checkout SDK."));
    document.body.appendChild(script);
  });
  return cashfreeSDKPromise;
}

/**
 * Redirect user to Cashfree checkout using the current SDK (Cashfree.js v3).
 * Directly navigating to a raw hosted-checkout URL is deprecated and returns
 * "client session is invalid" - the payment_session_id must be handed to the
 * SDK's checkout() call instead.
 *
 * @param {string} sessionId - payment_session_id from the order-creation response
 * @param {string} cashfreeMode - "sandbox" | "production", from the same response
 */
export async function redirectToCheckout(sessionId, cashfreeMode = "sandbox") {
  const Cashfree = await loadCashfreeSDK();
  const cashfree = Cashfree({ mode: cashfreeMode === "production" ? "production" : "sandbox" });
  cashfree.checkout({
    paymentSessionId: sessionId,
    redirectTarget: "_self",
  });
}

/** Format paise → ₹X,XX,XXX */
export function formatINR(paise) {
  return `₹${(paise / 100).toLocaleString("en-IN")}`;
}

/** Poll verifyPayment until status !== "pending", max 10 attempts × 2s = 20 seconds */
export async function pollPaymentStatus(orderId, onUpdate) {
  const MAX_ATTEMPTS = 10;

  for (let i = 0; i < MAX_ATTEMPTS; i++) {
    const remainingTime = (MAX_ATTEMPTS - i) * 2;
    onUpdate?.({ status: "polling", seconds_remaining: remainingTime });

    await new Promise((r) => setTimeout(r, 2000));
    try {
      const result = await verifyPayment(orderId);
      onUpdate?.(result);
      if (result.status !== "pending") return result;
    } catch {
      // network blip — keep polling
    }
  }
  return { status: "timeout", order_id: orderId };
}
